/**
 * Thẻ "Chất lượng tháng" (đợt 2a, CEO 17a): mỗi tiểu đội một dòng — số lượt
 * chăm khách phải làm / làm đúng hẹn, số khách đã giao ảnh / đã hỏi / hài
 * lòng. Người của tiểu đội không tự nhập cho mình (máy chủ chặn từng dòng).
 * Trưởng phòng KD / giám đốc giao thêm người nhập.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { StaffPicker } from "@/components/StaffPicker";
import { QueryErrorNote } from "@/components/QueryErrorNote";
import {
  type SalesOrgView,
  type SalesQualityInput,
  useQualityCheckers,
  useQualityMilestones,
  useQualitySurvey,
  useSalesQuality,
  useSaveQuality,
  useSaveQualityCheckers,
} from "../api";
import { squadsToEnter } from "../helpers";
import { PersonMark, SquadMark } from "./marks";
import { QualitySquadCard } from "./QualitySquadCard";

const FIELDS = [
  { key: "careDue", label: "Lượt chăm phải làm" },
  { key: "careOnTime", label: "Lượt chăm đúng hẹn" },
  { key: "delivered", label: "Khách đã giao ảnh" },
  { key: "surveyed", label: "Khách đã hỏi" },
  { key: "satisfied", label: "Khách hài lòng" },
] as const;

type FieldKey = (typeof FIELDS)[number]["key"];
type Row = Record<FieldKey, number | null> & { note: string };

const EMPTY: Row = { careDue: null, careOnTime: null, delivered: null, surveyed: null, satisfied: null, note: "" };

function parseCount(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

function Checkers() {
  const checkers = useQualityCheckers();
  const save = useSaveQualityCheckers();
  // Danh sách đang sửa đè lên danh sách máy chủ trả (null = chưa sửa gì).
  const [edited, setPicked] = useState<{ id: string; name: string }[] | null>(null);
  const picked = edited ?? (checkers.data ?? []).map((c) => ({ id: c.staffId, name: c.staffName }));
  const [adding, setAdding] = useState<string | null>(null);
  if (checkers.isError) return <QueryErrorNote error={checkers.error} fallback="Không tải được người nhập chất lượng" />;
  return (
    <section aria-labelledby="nguoi-nhap" className="space-y-2">
      <h3 id="nguoi-nhap" className="text-base font-semibold">
        Người được giao nhập chất lượng
      </h3>
      {picked.length === 0 ? <p className="text-sm text-muted">Chưa giao ai.</p> : null}
      <ul className="flex flex-wrap gap-2">
        {picked.map((p) => (
          <li key={p.id} className="flex items-center gap-1 rounded-full border border-border bg-surface py-1 pl-1 pr-1 text-sm">
            <PersonMark staffId={p.id} name={p.name || p.id} size={28} title={false} />
            <button
              type="button"
              aria-label={`Bỏ ${p.name || p.id}`}
              className="min-h-11 px-2 text-muted"
              onClick={() => setPicked(picked.filter((x) => x.id !== p.id))}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <div className="min-w-0 md:w-72">
          <StaffPicker
            value={adding}
            onChange={(id, name) => {
              setAdding(null);
              if (id && !picked.some((p) => p.id === id)) setPicked([...picked, { id, name: name ?? id }]);
            }}
            placeholder="Thêm người được giao"
          />
        </div>
        <Button
          disabled={save.isPending}
          onClick={() =>
            save.mutate(
              picked.map((p) => p.id),
              {
                onSuccess: () => {
                  setPicked(null);
                  toast.success("Đã lưu người được giao nhập chất lượng");
                },
                onError: (e) => toast.error(e instanceof Error ? e.message : "Không lưu được"),
              },
            )
          }
        >
          {save.isPending ? "Đang lưu…" : "Lưu người được giao"}
        </Button>
      </div>
    </section>
  );
}

type Props = { org: SalesOrgView; month: string };

export function QualityTab({ org, month }: Props) {
  const quality = useSalesQuality(month);
  const save = useSaveQuality(month);
  const squads = useMemo(
    () => squadsToEnter(org, month, new Set((quality.data ?? []).map((q) => q.squadId))),
    [org, month, quality.data],
  );
  const canRecord = org.me.canRecordQuality;
  const isAuto = (quality.data ?? []).some((q) => q.auto);
  const survey = useQualitySurvey(month, canRecord && isAuto);
  const milestones = useQualityMilestones(month, canRecord && isAuto);
  const teamOf = (parentId: string) => org.units.find((u) => u.id === parentId)?.name ?? "";
  // Dòng đang gõ đè lên số máy chủ trả — tải muộn hay tải lại không xoá chữ đang gõ dở.
  const [draft, setDraft] = useState<Record<string, Row>>({});
  const server = useMemo(() => {
    const out: Record<string, Row> = {};
    for (const q of quality.data ?? []) {
      out[q.squadId] = {
        careDue: q.careDue ?? null,
        careOnTime: q.careOnTime ?? null,
        delivered: q.delivered ?? null,
        surveyed: q.surveyed ?? null,
        satisfied: q.satisfied ?? null,
        note: q.note,
      };
    }
    return out;
  }, [quality.data]);
  const rowOf = (squadId: string): Row => draft[squadId] ?? server[squadId] ?? EMPTY;

  const setField = (squadId: string, key: FieldKey | "note", raw: string) =>
    setDraft((d) => {
      const row = d[squadId] ?? server[squadId] ?? EMPTY;
      return { ...d, [squadId]: key === "note" ? { ...row, note: raw } : { ...row, [key]: parseCount(raw) } };
    });

  const submit = () => {
    const items: SalesQualityInput[] = squads
      .filter((s) => draft[s.id] || server[s.id])
      .map((s) => ({ squadId: s.id, ...rowOf(s.id) }));
    if (items.length === 0) {
      toast.error("Chưa nhập số của tiểu đội nào");
      return;
    }
    save.mutate(items, {
      onSuccess: () => {
        setDraft({});
        toast.success("Đã lưu chất lượng tháng");
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : "Không lưu được chất lượng tháng"),
    });
  };

  const auto = (quality.data ?? []).some((q) => q.auto);
  if (auto && !quality.isError) {
    const marks = quality.data ?? [];
    return (
      <div className="space-y-6">
        <ul className="space-y-3">
          {marks.map((m) => {
            const unit = org.units.find((u) => u.id === m.squadId);
            return (
              <QualitySquadCard
                key={m.squadId}
                mark={m}
                name={unit?.name ?? m.squadId}
                teamName={unit ? teamOf(unit.parentId) : ""}
                month={month}
                canRecord={canRecord}
                survey={(survey.data ?? []).filter((s) => s.squadId === m.squadId)}
                surveyError={survey.isError ? survey.error : null}
                milestones={(milestones.data ?? []).filter((x) => x.squadId === m.squadId)}
                milestonesError={milestones.isError ? milestones.error : null}
              />
            );
          })}
        </ul>
        {org.me.canSetTargets ? <Checkers /> : null}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {quality.isError ? (
        <QueryErrorNote error={quality.error} fallback="Không tải được chất lượng tháng" />
      ) : squads.length === 0 ? (
        <p className="text-sm text-muted">Tháng này chưa có tiểu đội nào trong cơ cấu HRM gửi.</p>
      ) : (
        <ul className="space-y-3">
          {squads.map((s) => {
            const row = rowOf(s.id);
            const mark = (quality.data ?? []).find((q) => q.squadId === s.id);
            return (
              <li key={s.id} className="space-y-2 rounded-md border border-border bg-surface p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <SquadMark squadId={s.id} name={s.name} size={28} className="font-medium">
                    {s.name}
                    {/* Hai tiểu đội trùng tên ở hai đội khác nhau: ghi tên đội để khỏi nhập nhầm. */}
                    {teamOf(s.parentId) ? <span className="ml-1 text-xs font-normal text-muted">· {teamOf(s.parentId)}</span> : null}
                  </SquadMark>
                  {mark ? (
                    <span className="text-xs text-muted">
                      {mark.setByName || mark.setBy} nhập lúc{" "}
                      {new Date(mark.setAt).toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" })}
                    </span>
                  ) : (
                    <span className="text-xs text-muted">Chưa nhập</span>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
                  {FIELDS.map((f) => (
                    <label key={f.key} className="flex min-w-0 flex-col gap-1 text-sm">
                      <span className="text-muted">{f.label}</span>
                      {canRecord ? (
                        <Input
                          inputMode="numeric"
                          aria-label={`${f.label} — ${s.name}`}
                          value={row[f.key] === null ? "" : String(row[f.key])}
                          onChange={(e) => setField(s.id, f.key, e.target.value)}
                        />
                      ) : (
                        <span className="tabular-nums">{row[f.key] ?? "—"}</span>
                      )}
                    </label>
                  ))}
                </div>
                {canRecord ? (
                  <Input
                    aria-label={`Ghi chú — ${s.name}`}
                    placeholder="Ghi chú (không bắt buộc)"
                    value={row.note}
                    onChange={(e) => setField(s.id, "note", e.target.value)}
                  />
                ) : row.note ? (
                  <p className="text-sm text-muted">{row.note}</p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {canRecord && squads.length > 0 ? (
        <Button onClick={submit} disabled={save.isPending}>
          {save.isPending ? "Đang lưu…" : "Lưu chất lượng tháng"}
        </Button>
      ) : null}
      {org.me.canSetTargets ? <Checkers /> : null}
    </div>
  );
}
